import { storeToRefs } from 'pinia';
import { http, setHTTPHeader } from './http.service';
import { IUserLogin, IRegisterUser } from '@/types/general';
import { login, logout, register } from '@/apis/auth.api';
import { useUserStore } from '@/stores/user.store';

class AuthService {
  async login(user: IUserLogin) {
    return login(user).then((response) => {
      if (response.headers.authorization) {
        const token = response.headers.authorization;
        localStorage.setItem('user', JSON.stringify(response.data));
        localStorage.setItem('token', token);
        setHTTPHeader({ Authorization: token });
      }

      return response.data;
    });
  }

  async logout() {
    return logout().finally(() => {
      delete http.defaults.headers.common['Authorization'];
      this.clearCache();
    });
  }

  async register(user: IRegisterUser) {
    return register(user);
  }

  async resetPassword(email: string) {
    try {
      await http.put(`/api/v1/users/reset_password`, {
        email,
      });
    } catch (error) {
      return error;
    }
  }

  async updatePassword(params: object) {
    try {
      return await http.put(`/users/password`, params).then((response) => {
        return response.data;
      });
    } catch (error) {
      return error;
    }
  }

  clearCache(): void {
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    localStorage.removeItem('session');
    localStorage.removeItem('isImpersonating');
    location.href = '/';
  }

  getUser() {
    const user = localStorage.getItem('user');

    if (user) {
      try {
        return JSON.parse(user);
      } catch {
        return null;
      }
    }
  }

  isSevener() {
    if (this.getUser()) {
      const { currentUser } = storeToRefs(useUserStore());
      return currentUser?.value?.access_level === 'sevener';
    }
  }

  isAdmin() {
    if (this.getUser()) {
      const { currentUser } = storeToRefs(useUserStore());
      return currentUser?.value?.access_level === 'admin';
    }
  }

  isSuperAdmin() {
    if (this.getUser()) {
      const { currentUser } = storeToRefs(useUserStore());
      return currentUser?.value?.access_level === 'super_admin';
    }
  }

  isAdminOrAbove() {
    if (this.getUser()) {
      const { currentUser } = storeToRefs(useUserStore());
      return currentUser?.value?.access_level === 'admin' || currentUser?.value?.access_level === 'super_admin';
    }
  }

  getToken() {
    return localStorage.getItem('token');
  }
}

const instance = new AuthService();

export default instance;
